import { Group } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import {
  IconPencil,
  IconPlayerPause,
  IconPlayerPlay,
  IconReload,
  IconTrash
} from '@tabler/icons-react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { buildCreateDeploymentLink } from '@/router/paths'
import { DeploymentStatus } from '@/types/deployment'
import { DeploymentGroup } from '@/types/deploymentGroup'
import { CrudAction, Resource } from '@/types/permissions'
import { ActionButton } from '../DeploymentList/DeploymentListActions'
import { GroupDeleteConfirm } from './GroupDeleteConfirm'
import { GroupPauseConfirm } from './GroupPauseConfirm'
import { GroupRestartConfirm } from './GroupRestartConfirm'
import { DeploymentTreeGroupContext } from './deploymentTreeTypes'
import {
  canPauseGroup,
  canRestartGroup,
  canResumeGroup
} from './groupActionVisibility'

const iconStyle = { width: '70%', height: '70%' }

type DeploymentGroupActionsProps = {
  group: DeploymentGroup
  context: DeploymentTreeGroupContext
}

export const DeploymentGroupActions = ({
  group,
  context
}: DeploymentGroupActionsProps) => {
  const intl = useIntl()
  const navigate = useNavigate()

  const [isPauseOpen, { open: openPause, close: closePause }] =
    useDisclosure(false)
  const [isRestartOpen, { open: openRestart, close: closeRestart }] =
    useDisclosure(false)
  const [isDeleteOpen, { open: openDelete, close: closeDelete }] =
    useDisclosure(false)

  const [canUpdateDeployment, canDeleteDeployment] = useHasPermissions(
    Resource.Deployments,
    [CrudAction.Update, CrudAction.Delete]
  )

  return (
    <Group wrap="nowrap" gap={8} justify="end">
      {canUpdateDeployment && (
        <ActionButton
          tooltip={intl.formatMessage({
            id: 'deployments.list.buttons.edit.tooltip'
          })}
          icon={<IconPencil style={iconStyle} />}
          color="blue"
          onClick={() =>
            void navigate(buildCreateDeploymentLink(context.app.application_id))
          }
        />
      )}

      {canUpdateDeployment && canPauseGroup(group.status) && (
        <ActionButton
          tooltip={intl.formatMessage({
            id: 'deployments.groups.actions.pause.tooltip'
          })}
          icon={<IconPlayerPause style={iconStyle} />}
          color="yellow"
          onClick={openPause}
        />
      )}

      {canUpdateDeployment && canResumeGroup(group.status) && (
        <ActionButton
          tooltip={intl.formatMessage({
            id: 'deployments.groups.actions.resume.tooltip'
          })}
          icon={<IconPlayerPlay style={iconStyle} />}
          color="green"
          onClick={openPause}
        />
      )}

      {canUpdateDeployment && canRestartGroup(group.status) && (
        <ActionButton
          tooltip={intl.formatMessage({
            id: 'deployments.groups.actions.restart.tooltip'
          })}
          icon={<IconReload style={iconStyle} />}
          color="orange"
          onClick={openRestart}
        />
      )}

      {canDeleteDeployment && (
        <ActionButton
          tooltip={intl.formatMessage({
            id: 'deployments.groups.actions.delete.tooltip'
          })}
          icon={<IconTrash style={iconStyle} />}
          color="red"
          onClick={openDelete}
        />
      )}

      <GroupPauseConfirm
        opened={isPauseOpen}
        group={group}
        context={context}
        isPaused={group.status === DeploymentStatus.Stopped}
        onClose={closePause}
      />

      <GroupRestartConfirm
        opened={isRestartOpen}
        group={group}
        context={context}
        onClose={closeRestart}
      />

      <GroupDeleteConfirm
        opened={isDeleteOpen}
        group={group}
        context={context}
        onClose={closeDelete}
      />
    </Group>
  )
}
